import React, { useState, useEffect } from "react";
import HeaderComponent from "../../Header";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaTimes } from "react-icons/fa";

const ACTIVE_STATUSES = ["active", "past_due"];

// Shown only once enforcement is on, so the dashboard is unchanged before launch.
const BillingNotice = ({ billing }) => {
  const [dismissed, setDismissed] = useState(false);

  if (!billing || !billing.enforcement || dismissed) return null;

  const status = billing.status;
  const usage = billing.usage || {};
  const subscribed = ACTIVE_STATUSES.includes(status) || (status === "cancelled" && billing.paid_through);

  if (subscribed) {
    const remaining = usage.remaining || 0;
    const exhausted = remaining === 0;

    return (
      <p className={`mb-6 text-sm ${exhausted ? "text-red-600 font-medium" : "text-gray-500"}`}>
        {remaining} of {usage.limit || 0} certificates left this period.{" "}
        {exhausted && (
          <>
            Your allowance resets when your subscription renews —{" "}
            <a href="/users/billing" className="underline">
              view billing
            </a>
            .
          </>
        )}
      </p>
    );
  }

  return (
    <div className="mb-6 flex items-start justify-between bg-blue-50 border border-blue-300 text-blue-800 rounded-lg p-4">
      <p className="text-sm">
        {status === "none"
          ? "Subscribe to generate certificates."
          : "Your subscription is not active, so certificate generation is paused."}{" "}
        <a href="/users/billing" className="font-semibold underline">
          Go to billing
        </a>
      </p>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label="Dismiss"
        className="ml-4 text-blue-800 hover:text-blue-900"
      >
        <FaTimes />
      </button>
    </div>
  );
};

const App = (props) => {
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simulate fetch delay
    setTimeout(() => {
      const sortedCertificates = [...props.certificates].sort((a, b) =>
        new Date(b.created_at) - new Date(a.created_at) // Descending order: recent first
      );
      setCertificates(sortedCertificates);
      setLoading(false);
    }, 1000);
  }, [props]);

  return (
    <div className="bg-gray-50 min-h-screen">
      <HeaderComponent />
      <div className="container mx-auto py-8 px-4">
        <BillingNotice billing={props.billing} />
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {Array(6)
              .fill()
              .map((_, index) => (
                <div
                  key={index}
                  className="bg-white shadow-lg rounded-lg animate-pulse"
                >
                  <div className="aspect-w-16 aspect-h-9 bg-gray-200 rounded-t-lg"></div>
                  <div className="p-6">
                    <div className="h-6 bg-gray-200 rounded mb-4"></div>
                    <div className="h-4 bg-gray-200 rounded"></div>
                  </div>
                </div>
              ))}
          </div>
        ) : certificates.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {certificates.map((design) => (
              <a
                key={design.id}
                href={`/designs/${design.id}`}
                className="bg-white shadow-lg rounded-lg hover:shadow-2xl transition-transform transform hover:scale-105 relative"
                style={{ textDecoration: "none" }}
              >
                <div className="aspect-w-16 aspect-h-9 rounded-t-lg overflow-hidden">
                  <LazyLoadImage
                    alt={design.name}
                    className="w-full h-full object-cover"
                    height={"100%"}
                    width={"100%"}
                    src={design.cover_image ? `${design.cover_image}?q=${design.updated_at}` : "/placeholder.png"}
                  />
                </div>
                <div className="p-6">
                  <h2 className="text-xl font-bold text-gray-800 mb-2 truncate">
                    {design.name || "Untitled"}
                  </h2>
                </div>
                <span
                  className={`absolute top-2 right-2 px-3 py-1 text-sm font-medium rounded-full ${design.published
                    ? "bg-green-100 text-green-800"
                    : "bg-red-100 text-red-800"
                    }`}
                >
                  {design.published ? "Published" : "Unpublished"}
                </span>
              </a>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <h2 className="text-2xl font-semibold text-gray-700">
              No certificates available
            </h2>
            <p className="text-gray-500 mt-4">
              Add some designs to get started!
            </p>
          </div>
        )}
      </div>
      <ToastContainer />
    </div>
  );
};

export default App;
